import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState, type ReactElement } from "react";
import { Field, Modal, useToast } from "@cx/ui";
import { BRAZIL_UFS, formatBRL, formatDateBR, parseCollectionBarcode, todayInSaoPaulo } from "@cx/domain";
import type { FinancialAccountView, GnreSettingsView, GnreView } from "@cx/contracts";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";
import { useOpenKey } from "../invoices/InvoiceEvents";
import { guideTitle } from "./labels";

/**
 * Diálogos da guia (T2.11): vencimento, emitir (linha digitável conferida no navegador e na API), pagar (baixa da conta
 * a pagar + comprovante opcional), comprovante, cancelar (estorno) e configuração. Uma Idempotency-Key por abertura.
 */

type GuideDialogProps = { open: boolean; guide: GnreView; onClose(): void; onDone(g: GnreView): void };

/** Mensagem do código de barras digitado (null = confere). */
export function barcodeProblem(input: string, amountCents: number): string | null {
  if (!input.trim()) return "Informe a linha digitável ou o código de barras";
  try {
    const p = parseCollectionBarcode(input);
    if (p.amountCents !== null && p.amountCents !== amountCents)
      return `O código é de ${formatBRL(p.amountCents)}; a guia é de ${formatBRL(amountCents)}`;
    return null;
  } catch (e) {
    return (e as Error).message;
  }
}

export function IssueDialog({ open, guide, onClose, onDone }: GuideDialogProps): ReactElement {
  const { api } = useSession();
  const toast = useToast();
  const [barcode, setBarcode] = useState("");
  const [control, setControl] = useState("");
  const [touched, setTouched] = useState(false);
  const { key, renew } = useOpenKey(open);
  useEffect(() => {
    if (open) {
      setBarcode(guide.digitableLine ?? "");
      setControl(guide.controlNumber ?? "");
      setTouched(false);
    }
  }, [open, guide.digitableLine, guide.controlNumber]);
  const problem = barcodeProblem(barcode, guide.amountCents);
  const send = useMutation({
    mutationFn: () =>
      api<GnreView>(`/gnre/${guide.id}/issue`, {
        method: "POST",
        headers: { "Idempotency-Key": key },
        body: JSON.stringify({ barcode, controlNumber: control || null }),
      }),
    onSuccess: (g) => {
      toast("success", "Guia marcada como emitida");
      onDone(g);
    },
    onError: (e) => {
      toast("danger", errorMessage(e));
      renew(e);
    },
  });
  return (
    <Modal
      open={open}
      title="Marcar como emitida"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
            Voltar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={send.isPending}
            onClick={() => (problem ? setTouched(true) : send.mutate())}
          >
            Salvar
          </button>
        </>
      }
    >
      <p className="small">
        Emita a guia no portal ({guide.kind === "dare_sp" ? "SEFAZ-SP" : "Portal GNRE"}) e copie a linha
        digitável (48 dígitos) ou o código de barras (44). O valor do código precisa ser{" "}
        {formatBRL(guide.amountCents)}.
      </p>
      <div className="row">
        <Field
          label="Linha digitável ou código de barras"
          required
          className="col-12"
          error={touched ? (problem ?? undefined) : undefined}
        >
          <input
            className="form-control font-monospace"
            data-autofocus
            inputMode="numeric"
            value={barcode}
            onChange={(e) => setBarcode(e.target.value)}
            onBlur={() => setTouched(true)}
          />
        </Field>
        <Field label="Número de controle" className="col-md-6" hint="Opcional, como aparece na guia.">
          <input
            className="form-control"
            maxLength={30}
            value={control}
            onChange={(e) => setControl(e.target.value)}
          />
        </Field>
      </div>
    </Modal>
  );
}

/** Envia o comprovante (PDF, JPG ou PNG até 5 MB). */
export function useProofUpload(guideId: string) {
  const { api } = useSession();
  return useMutation({
    mutationFn: (file: File) => {
      const fd = new FormData();
      fd.append("file", file);
      return api<GnreView>(`/gnre/${guideId}/proof`, { method: "POST", body: fd });
    },
  });
}

export function PayDialog({ open, guide, onClose, onDone }: GuideDialogProps): ReactElement {
  const { api, companyId } = useSession();
  const toast = useToast();
  const [paidAt, setPaidAt] = useState(todayInSaoPaulo());
  const [accountId, setAccountId] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const { key, renew } = useOpenKey(open);
  const accounts = useQuery({
    queryKey: ["fin-accounts", companyId, "list"],
    enabled: open,
    queryFn: () => api<{ data: FinancialAccountView[] }>("/financial-accounts"),
  });
  const options = (accounts.data?.data ?? []).filter((a) => a.active);
  useEffect(() => {
    if (open) {
      setPaidAt(todayInSaoPaulo());
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
    }
  }, [open]);
  useEffect(() => {
    if (!accountId && options.length) setAccountId(options[0]!.id);
  }, [accountId, options]);
  const proof = useProofUpload(guide.id);
  const send = useMutation({
    mutationFn: async () => {
      const g = await api<GnreView>(`/gnre/${guide.id}/pay`, {
        method: "POST",
        headers: { "Idempotency-Key": key },
        body: JSON.stringify({ paidAt, financialAccountId: accountId }),
      });
      if (!file) return g;
      try {
        return await proof.mutateAsync(file);
      } catch (e) {
        toast("warning", `Guia paga, mas o comprovante não foi enviado: ${errorMessage(e)}`);
        return g;
      }
    },
    onSuccess: (g) => {
      toast("success", "Guia paga e conta a pagar baixada");
      onDone(g);
    },
    onError: (e) => {
      toast("danger", errorMessage(e));
      renew(e);
    },
  });
  return (
    <Modal
      open={open}
      title="Marcar como paga"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
            Voltar
          </button>
          <button
            type="button"
            className="btn btn-success"
            disabled={send.isPending || !accountId || !paidAt}
            onClick={() => send.mutate()}
          >
            Confirmar pagamento
          </button>
        </>
      }
    >
      <p className="small">
        Baixa a conta a pagar da guia ({formatBRL(guide.amountCents)}, vencimento{" "}
        {formatDateBR(guide.dueDate)}) na conta escolhida.
      </p>
      <div className="row">
        <Field label="Data do pagamento" required className="col-md-5">
          <input
            type="date"
            className="form-control"
            data-autofocus
            value={paidAt}
            max={todayInSaoPaulo()}
            onChange={(e) => setPaidAt(e.target.value)}
          />
        </Field>
        <Field label="Conta" required className="col-md-7">
          <select className="form-select" value={accountId} onChange={(e) => setAccountId(e.target.value)}>
            {options.length === 0 && <option value="">Nenhuma conta financeira</option>}
            {options.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Comprovante" className="col-12" hint="PDF, JPG ou PNG até 5 MB (opcional).">
          <input
            ref={fileRef}
            type="file"
            className="form-control"
            accept="application/pdf,image/jpeg,image/png"
            data-testid="gnre-proof-file"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </Field>
      </div>
    </Modal>
  );
}

export function DueDateDialog({ open, guide, onClose, onDone }: GuideDialogProps): ReactElement {
  const { api } = useSession();
  const toast = useToast();
  const [due, setDue] = useState(guide.dueDate);
  useEffect(() => {
    if (open) setDue(guide.dueDate);
  }, [open, guide.dueDate]);
  const send = useMutation({
    mutationFn: () =>
      api<GnreView>(`/gnre/${guide.id}`, { method: "PUT", body: JSON.stringify({ dueDate: due }) }),
    onSuccess: (g) => {
      toast("success", "Vencimento alterado");
      onDone(g);
    },
    onError: (e) => toast("danger", errorMessage(e)),
  });
  return (
    <Modal
      open={open}
      title="Alterar vencimento"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
            Voltar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={send.isPending || !due}
            onClick={() => send.mutate()}
          >
            Salvar
          </button>
        </>
      }
    >
      <p className="small">A conta a pagar da guia (sem baixa) acompanha o novo vencimento.</p>
      <div className="row">
        <Field label="Vencimento" required className="col-md-6">
          <input
            type="date"
            className="form-control"
            data-autofocus
            value={due}
            onChange={(e) => setDue(e.target.value)}
          />
        </Field>
      </div>
    </Modal>
  );
}

export function CancelGuideDialog({ open, guide, onClose, onDone }: GuideDialogProps): ReactElement {
  const { api } = useSession();
  const toast = useToast();
  const [reason, setReason] = useState("");
  const [touched, setTouched] = useState(false);
  const { key, renew } = useOpenKey(open);
  useEffect(() => {
    if (open) {
      setReason("");
      setTouched(false);
    }
  }, [open]);
  const problem =
    reason.trim().length < 5
      ? "Motivo: ao menos 5 caracteres"
      : reason.trim().length > 255
        ? "Até 255 caracteres"
        : null;
  const send = useMutation({
    mutationFn: () =>
      api<GnreView>(`/gnre/${guide.id}/cancel`, {
        method: "POST",
        headers: { "Idempotency-Key": key },
        body: JSON.stringify({ reason }),
      }),
    onSuccess: (g) => {
      toast("info", "Guia cancelada e conta a pagar estornada");
      onDone(g);
    },
    onError: (e) => {
      toast("danger", errorMessage(e));
      renew(e);
    },
  });
  return (
    <Modal
      open={open}
      title={`Cancelar ${guideTitle(guide)}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
            Voltar
          </button>
          <button
            type="button"
            className="btn btn-danger"
            disabled={send.isPending}
            onClick={() => (problem ? setTouched(true) : send.mutate())}
          >
            Cancelar guia
          </button>
        </>
      }
    >
      <p className="small">
        A guia fica no histórico como cancelada e a conta a pagar dela é cancelada (estorno). Guia já emitida
        no portal e não paga simplesmente não deve ser paga.
      </p>
      <div className="row">
        <Field
          label="Motivo"
          required
          className="col-12"
          error={touched ? (problem ?? undefined) : undefined}
        >
          <textarea
            className="form-control"
            rows={2}
            data-autofocus
            maxLength={255}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </Field>
      </div>
    </Modal>
  );
}

type Reg = { uf: string; ie: string };

export function GnreSettingsDialog({ open, onClose }: { open: boolean; onClose(): void }): ReactElement {
  const { api, companyId } = useSession();
  const toast = useToast();
  const settings = useQuery({
    queryKey: ["gnre-settings", companyId],
    enabled: open,
    queryFn: () => api<GnreSettingsView>("/gnre/settings"),
  });
  const [regs, setRegs] = useState<Reg[]>([]);
  const [spGuide, setSpGuide] = useState<"gnre" | "dare_sp">("gnre");
  useEffect(() => {
    if (open && settings.data) {
      setRegs(settings.data.stRegistrations);
      setSpGuide(settings.data.spGuide);
    }
  }, [open, settings.data]);
  const save = useMutation({
    mutationFn: () =>
      api<GnreSettingsView>("/gnre/settings", {
        method: "PUT",
        body: JSON.stringify({ stRegistrations: regs.filter((r) => r.ie.trim()), spGuide }),
      }),
    onSuccess: () => {
      toast("success", "Configuração das guias salva");
      void settings.refetch();
      onClose();
    },
    onError: (e) => toast("danger", errorMessage(e)),
  });
  const set = (k: number, v: Partial<Reg>) =>
    setRegs((rs) => rs.map((r, i) => (i === k ? { ...r, ...v } : r)));
  return (
    <Modal
      open={open}
      title="Configuração das guias GNRE/DARE"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
            Voltar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={save.isPending}
            onClick={() => save.mutate()}
          >
            Salvar
          </button>
        </>
      }
    >
      <h3 className="h6">Inscrição estadual de substituto tributário</h3>
      <p className="small text-muted">
        Nas UFs em que a empresa tem IE de substituto, o ICMS-ST/DIFAL é recolhido por apuração: a NF-e não
        gera guia.
      </p>
      <table className="table table-sm align-middle" aria-label="IE de substituto por UF">
        <thead>
          <tr>
            <th scope="col">UF</th>
            <th scope="col">Inscrição estadual</th>
            <th scope="col" className="text-end">
              <span className="visually-hidden">Ações</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {regs.map((r, k) => (
            <tr key={k}>
              <td style={{ width: "7rem" }}>
                <select
                  className="form-select form-select-sm"
                  aria-label="UF"
                  value={r.uf}
                  onChange={(e) => set(k, { uf: e.target.value })}
                >
                  {BRAZIL_UFS.map((uf) => (
                    <option key={uf} value={uf}>
                      {uf}
                    </option>
                  ))}
                </select>
              </td>
              <td>
                <input
                  className="form-control form-control-sm"
                  aria-label={`Inscrição estadual em ${r.uf}`}
                  value={r.ie}
                  onChange={(e) => set(k, { ie: e.target.value })}
                />
              </td>
              <td className="text-end">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => setRegs((rs) => rs.filter((_, i) => i !== k))}
                >
                  Remover
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button
        type="button"
        className="btn btn-sm btn-outline-primary mb-3"
        onClick={() =>
          setRegs((rs) => [
            ...rs,
            { uf: BRAZIL_UFS.find((u) => !rs.some((r) => r.uf === u)) ?? "AC", ie: "" },
          ])
        }
      >
        Adicionar UF
      </button>
      <div className="row">
        <Field label="Guia para São Paulo" className="col-md-6">
          <select
            className="form-select"
            value={spGuide}
            onChange={(e) => setSpGuide(e.target.value as "gnre" | "dare_sp")}
          >
            <option value="gnre">GNRE</option>
            <option value="dare_sp">DARE-SP</option>
          </select>
        </Field>
      </div>
    </Modal>
  );
}
